import React from "react";

export const TextFormatter = ({ text, onLinkClick, style }) => {
  if (!text) return;

  // Split the text into words
  const words = text.split(" ");

  return words.map((word, index) => {
    // Added case for TM
    if (word.includes("TM")) {
      const tmIndex = word.toLowerCase().indexOf("tm");
      return (
        <span key={index}>
          {word.slice(0, tmIndex)}
          <sup>{word.slice(tmIndex, tmIndex + 2).toUpperCase()}</sup>
          {word.slice(tmIndex + 2)}{" "}
        </span>
      );
    }
    // Define your word-specific styling conditions
    if (word.startsWith("__") && word.endsWith("__")) {
      return <u key={index}>{word.slice(2, -2)}</u>;
    }

    if (word.startsWith("\n\n")) {
      return (
        <span key={index} style={{ color: "var(--color-primary", ...style }}>
          {word.slice(1)}{" "}
        </span>
      );
    }
    if (word.startsWith("</br>")) {
      return (
        <span key={index} style={{ color: "var(--color-primary", ...style }}>
          {word.slice(1)}{" "}
        </span>
      );
    } else if (word.startsWith("@")) {
      // Blue color for words starting with @
      return (
        <span key={index} style={{ color: "var(--color-primary", ...style }}>
          {word.slice(1)}{" "}
        </span>
      );
    } else if (word.startsWith("*")) {
      // Bold for words starting with *
      return (
        <span key={index} style={{ fontWeight: "bold", ...style }}>
          {word.slice(1)}{" "}
        </span>
      );
    } else if (
      word.startsWith("http") ||
      word.startsWith("www") ||
      word.startsWith("#")
    ) {
      // Clickable links
      return (
        <a
          key={index}
          href={word}
          style={{ color: "#0066cc", ...style }}
          onClick={(e) => {
            e.preventDefault();
            onLinkClick && onLinkClick(word);
          }}
        >
          {word.slice(1)}{" "}
        </a>
      );
    }
    // Default case: return the word as is
    return (
      <span key={index} style={{ ...style }}>
        {word}{" "}
      </span>
    );
  });
};

export const FormatFaqAnswer = ({ answer, handleLinkClick }) => {
  if (!answer) return null;

  // **Fix: Convert escaped newlines into real newlines**
  const formattedAnswer = answer.replace(/\\n/g, "\n");

  // Split by double newlines (paragraph separation)
  const sections = formattedAnswer.split(/\n\s*\n/);

  return (
    <div>
      {sections.map((section, index) => {
        const trimmedSection = section.trim();

        // **Detect numbered lists correctly**
        if (/^\d+\./.test(trimmedSection)) {
          const [title, ...rest] = trimmedSection.split("\n");
          return (
            <div key={index} style={{ marginBottom: "10px" }}>
              <p style={{ fontWeight: "bold", fontSize: "14px" }}>
                <strong>{title.trim()}</strong>
              </p>
              <ul
                style={{
                  paddingLeft: "40px",
                  listStyleType: "disc",
                  margin: "0",
                }}
              >
                <li>
                  <p style={{ margin: "0" }}>
                    <TextFormatter
                      text={rest.join(" ").trim()}
                      onLinkClick={handleLinkClick}
                    />
                  </p>
                </li>
              </ul>
            </div>
          );
        }

        // Default paragraph formatting
        return (
          <p key={index} style={{ marginBottom: "8px" }}>
            {trimmedSection}
          </p>
        );
      })}
    </div>
  );
};

export const formattedText = (text) => {
  if (!text) return null;

  // Replace any escaped \n (\\n) with a real newline (\n)
  const formattedText = text.replace(/\\n/g, "\n");

  // Split by real newlines now
  const textLines = formattedText.split("\n");
  return textLines.map((str, index) => (
    <React.Fragment key={index}>
      {str}
      {index < textLines.length - 1 && <br />}
    </React.Fragment>
  ));
};

export const mixedFormattedText = (text, style) => {
  if (!text) return null;

  // Step 1: Normalize double line breaks (e.g., \n\s or \n \n) to a unique marker
  let normalizedText = text
    .replace(/\\n\\s/g, "[[DOUBLE_BREAK]]") // \n\s
    .replace(/\\n\s/g, "[[DOUBLE_BREAK]]") // \n followed by space
    .replace(/\\n\\n/g, "[[DOUBLE_BREAK]]") // \n\n
    .replace(/\\n/g, "[[SINGLE_BREAK]]"); // single \n

  // Step 2: Split into paragraphs by double breaks
  const paragraphs = normalizedText.split("[[DOUBLE_BREAK]]");

  return paragraphs.map((para, i) => {
    // Split each paragraph by single breaks and apply <br />
    const lines = para.split("[[SINGLE_BREAK]]");

    return (
      <p key={i} style={{ marginBottom: "1rem", ...style }}>
        {lines.map((line, j) => (
          <React.Fragment key={j}>
            <TextFormatter text={line.trim()} onLinkClick={() => {}} />
            {j < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  });
};

// Helper to parse underline formatting (e.g., __text__)
const formatUnderline = (line) => {
  const parts = line.split(/(__.*?__)/);

  return parts.map((part, i) => {
    if (part.startsWith("__") && part.endsWith("__")) {
      return <u key={i}>{part.slice(2, -2)}</u>;
    }
    return <span key={i}>{part}</span>;
  });
};

export const FormatTextWithTable = ({ answer, tableData, handleLinkClick }) => {
  // Clean the data to remove extra quotes or unexpected characters (if any)
  const cleanedData = tableData?.replace(/^'(.*)'$/, "$1");

  // Parse the cleaned string
  let tData = [];
  try {
    tData = JSON.parse(cleanedData);
  } catch (error) {
    console.error("Error parsing JSON:", error);
  }

  if (!answer) return null;

  const formattedAnswer = answer.replace(/\\n/g, "\n");
  const sections = formattedAnswer.split(/\n\s*\n/);

  return (
    <div>
      {sections.map((section, index) => {
        const trimmedSection = section.trim();

        // Render table for Sale Deposit values
        if (
          trimmedSection.includes("Appraised Value of Property") &&
          tableData?.length > 0
        ) {
          return (
            <div key={index} style={{ marginBottom: "18px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                      Appraised Value of Property
                    </th>
                    <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                      Sale Deposit
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tData?.map((row, index) => (
                    <tr key={index}>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        {row.range}
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        {row.deposit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        if (/^\d+\./.test(trimmedSection)) {
          const [title, ...rest] = trimmedSection.split("\n");
          return (
            <div key={index} style={{ marginBottom: "10px" }}>
              <p style={{ fontWeight: "bold", fontSize: "14px" }}>
                <strong>{title.trim()}</strong>
              </p>
              <ul
                style={{
                  paddingLeft: "40px",
                  listStyleType: "disc",
                  margin: "0",
                }}
              >
                <li>
                  <p style={{ margin: "0" }}>
                    <TextFormatter
                      text={rest.join(" ").trim()}
                      onLinkClick={handleLinkClick}
                    />
                  </p>
                </li>
              </ul>
            </div>
          );
        }

        return (
          <p key={index} style={{ marginBottom: "8px" }}>
            <TextFormatter
              text={trimmedSection}
              onLinkClick={handleLinkClick}
            />
            {/* {trimmedSection} */}
          </p>
        );
      })}
    </div>
  );
};
